import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCanvasAccountRoles,
  fetchStaffCanvasStatus,
  provisionStaffCanvasGrants,
  revokeStaffCanvasGrants,
} from '../../services/lms-provisioning.service';
import { CANVAS_ACCESS_BULK_MODE } from '../../constants/canvas-access.constants';
import useCanvasAccessBulkBridge from '../StaffCanvasAccessBulkBar/useCanvasAccessBulkBridge';
import {
  buildCanvasAccessSummary,
  buildRevokeLocationKeys,
  buildRowRemoveConfirmMessage,
  getCanvasReadyLocations,
  getProvisionedLocationCount,
  getRevokeFailureMessage,
  hasLoadedRoleOptions,
  resolveCanvasRoleLabel,
  normalizeCanvasRoleId,
} from '../utils/canvas-access.utils';

/**
 * Orchestrates Canvas Access tab data, row editing, remove, and bulk provisioning state.
 * Bulk selection/draft state is owned by StaffCanvasAccessBulkBarContainer and bridged here.
 * @param {string|number|null} personId
 * @param {{ staffName?: string|null }} [options]
 */
const useStaffCanvasAccessHook = (personId, { staffName = null } = {}) => {
  const [canvasAccessSnapshot, setCanvasAccessSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingLocationKey, setEditingLocationKey] = useState(null);
  const [rolesLoadingLocationKey, setRolesLoadingLocationKey] = useState(null);
  const [roleOptionsByLocation, setRoleOptionsByLocation] = useState({});
  const [rowRemoveTarget, setRowRemoveTarget] = useState(null);
  const refreshInFlightRef = useRef(false);
  const { bulkApi, setBulkApi } = useCanvasAccessBulkBridge();

  const locations = useMemo(
    () => canvasAccessSnapshot?.locations ?? [],
    [canvasAccessSnapshot?.locations],
  );

  const eligibility = canvasAccessSnapshot?.eligibility ?? null;

  const canvasReadyLocations = useMemo(
    () => getCanvasReadyLocations(locations),
    [locations],
  );

  const provisionedCount = useMemo(
    () => getProvisionedLocationCount(canvasReadyLocations),
    [canvasReadyLocations],
  );

  const summary = useMemo(
    () => buildCanvasAccessSummary(canvasReadyLocations.length, locations.length, provisionedCount),
    [canvasReadyLocations.length, locations.length, provisionedCount],
  );

  const refresh = useCallback(async () => {
    if (personId == null || refreshInFlightRef.current) {
      return null;
    }

    refreshInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const snapshot = await fetchStaffCanvasStatus(personId);
      setCanvasAccessSnapshot(snapshot);
      return snapshot;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      refreshInFlightRef.current = false;
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    if (personId == null) {
      return undefined;
    }

    setRoleOptionsByLocation({});
    refresh();
    return undefined;
  }, [personId, refresh]);

  const loadRolesForLocation = useCallback(async (locationId) => {
    let cachedRoles;
    setRoleOptionsByLocation((prev) => {
      if (hasLoadedRoleOptions(prev, locationId)) {
        cachedRoles = prev[locationId];
      }
      return prev;
    });

    if (cachedRoles) {
      return cachedRoles;
    }

    const response = await fetchCanvasAccountRoles(locationId);
    setRoleOptionsByLocation((prev) => ({ ...prev, [locationId]: response.roles }));
    return response.roles;
  }, []);

  const clearRowEdit = useCallback(() => {
    setEditingLocationKey(null);
  }, []);

  const bulkMode = bulkApi?.bulkMode ?? CANVAS_ACCESS_BULK_MODE.IDLE;

  const rowActionsDisabled =
    editingLocationKey != null || bulkMode !== CANVAS_ACCESS_BULK_MODE.IDLE || submitting;

  const startRowEdit = useCallback(async (locationKey, currentRoleId = null) => {
    bulkApi?.clearBulkForRowEdit();
    setRowRemoveTarget(null);
    setError(null);
    setRolesLoadingLocationKey(locationKey);
    try {
      await loadRolesForLocation(locationKey);
      setEditingLocationKey(locationKey);
      bulkApi?.setRowDraftRole(locationKey, currentRoleId);
    } catch (err) {
      setError(err);
    } finally {
      setRolesLoadingLocationKey(null);
    }
  }, [bulkApi, loadRolesForLocation]);

  const saveRowEdit = useCallback(async (locationKey) => {
    const canvasRoleId = normalizeCanvasRoleId(bulkApi?.getRowDraftRole(locationKey));
    const canvasRoleLabel = resolveCanvasRoleLabel(roleOptionsByLocation, locationKey, canvasRoleId);
    if (!canvasRoleId || !canvasRoleLabel) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await provisionStaffCanvasGrants(personId, [{ locationKey, canvasRoleId, canvasRoleLabel }]);
      clearRowEdit();
      await refresh();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [bulkApi, clearRowEdit, personId, refresh, roleOptionsByLocation]);

  const requestRemoveRowAccess = useCallback((row) => {
    if (!row?.provisioned) {
      return;
    }

    bulkApi?.clearBulkForRowEdit();
    clearRowEdit();
    setError(null);
    setRowRemoveTarget(row);
  }, [bulkApi, clearRowEdit]);

  const cancelRemoveRowAccess = useCallback(() => {
    if (submitting) {
      return;
    }
    setRowRemoveTarget(null);
  }, [submitting]);

  const confirmRemoveRowAccess = useCallback(async () => {
    if (!rowRemoveTarget?.provisioned) {
      return;
    }

    const locationKeys = buildRevokeLocationKeys([rowRemoveTarget]);
    setSubmitting(true);
    setError(null);
    try {
      const response = await revokeStaffCanvasGrants(personId, locationKeys);
      const partialMessage = getRevokeFailureMessage(response);
      setRowRemoveTarget(null);
      await refresh();
      if (partialMessage) {
        setError(new Error(partialMessage));
      }
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [personId, refresh, rowRemoveTarget]);

  const onToggleRowSelected = useCallback((locationKey) => {
    bulkApi?.toggleSelected(locationKey);
  }, [bulkApi]);

  const onRowDraftRoleChange = useCallback((locationKey, roleId) => {
    bulkApi?.setRowDraftRole(locationKey, roleId);
  }, [bulkApi]);

  const showBulkBar = editingLocationKey == null && eligibility?.status !== 'INELIGIBLE';
  const errorMessage = error?.message || 'Failed to update Canvas access.';

  const rowRemoveConfirmMessage = useMemo(() => {
    if (!rowRemoveTarget) {
      return '';
    }

    return buildRowRemoveConfirmMessage({
      staffName,
      locationName: rowRemoveTarget.locationName,
      locationKey: rowRemoveTarget.locationKey,
      roleLabel: rowRemoveTarget.canvasRoleLabel,
    });
  }, [rowRemoveTarget, staffName]);

  const bulkBarContainerProps = useMemo(() => ({
    personId,
    staffName,
    locations,
    roleOptionsByLocation,
    loadRolesForLocation,
    refresh,
    onClearRowEdit: clearRowEdit,
    onSetSubmitting: setSubmitting,
    onSetError: setError,
    submitting,
    setBulkApi,
  }), [
    clearRowEdit,
    loadRolesForLocation,
    locations,
    personId,
    refresh,
    roleOptionsByLocation,
    setBulkApi,
    staffName,
    submitting,
  ]);

  return {
    bulkApi,
    bulkBarContainerProps,
    cancelRemoveRowAccess,
    confirmRemoveRowAccess,
    editingLocationKey,
    eligibility,
    error,
    errorMessage,
    loading,
    locations,
    onCancelRowEdit: clearRowEdit,
    onLoadRolesForLocation: loadRolesForLocation,
    onRequestRemoveRowAccess: requestRemoveRowAccess,
    onRowDraftRoleChange,
    onSaveRowEdit: saveRowEdit,
    onStartRowEdit: startRowEdit,
    onToggleRowSelected,
    refresh,
    roleOptionsByLocation,
    rolesLoadingLocationKey,
    rowActionsDisabled,
    rowRemoveConfirmMessage,
    rowRemoveOpen: rowRemoveTarget != null,
    showBulkBar,
    submitting,
    summary,
  };
};

export default useStaffCanvasAccessHook;
